import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Listing } from '../../../../../../../models/Listing';
import { GenerateHtmlFunction, ListingPreviewElementsContent } from '../utils';
import { Field, useField } from 'react-final-form';
import { DropzoneFile } from 'components/Dropzone/utils';
import { Button, ImageSlider } from 'ncoded-component-library';
import ResolveImage from 'components/ResolveImage';
import {
  convertMoneyToNumber,
  isEnterPressed,
  mapValueToMoneyFormat,
} from 'utils';
import TextareaArrayField from 'components/TextareaArrayField';
import ListingSlugField, {
  SLUG_FIELD_NAME,
} from 'router/subrouters/ListingCreationWizard/components/ListingSlugField/ListingSlugField.component';
import HighlightsSection from 'router/subrouters/Dash/subrouters/Business/pages/Business/components/HighlightsSection';
import { AdvertisingBusinessCardProps } from 'components/AdvertisingBusinessCard/AdvertisingBusinessCard.component';
import PhoneIcon from 'icons/Phone.icon';
import LinkIcon from 'icons/Link.icon';
import { formatPhoneNumber } from 'phoneNumberUtils';

import './DetailPageField.styles.scss';

type DetailPageFieldProps = {
  className?: string;
  name: keyof Listing | keyof AdvertisingBusinessCardProps;
  field?: any;
  previewType: keyof ListingPreviewElementsContent;
  icon?: React.FC;
  placeholder?: string;
  label?: string;
  allowEnter?: boolean;
};

const FIELD_PREVIEW_ELEMENT: GenerateHtmlFunction<ListingPreviewElementsContent> =
  {
    h1: (val: string) => <h1>{val}</h1>,
    images: (images: DropzoneFile[]) => (
      <div className="res-ldp-image-container">
        {Array.isArray(images) ? (
          images?.length > 1 ? (
            <ImageSlider
              slides={images.map(({ url }) => ({
                imageUrl: url,
              }))}
              showDots={!!images?.length}
              className="listing-card-resolve__image-slider-container__slider"
            />
          ) : (
            <ResolveImage
              className="listing-card-image"
              src={images?.[0]?.url}
            />
          )
        ) : (
          <ResolveImage
            className="listing-card-image"
            src={(images as DropzoneFile)?.url}
          />
        )}
      </div>
    ),
    link: (link: string) => <p>{link}</p>,
    phoneNumber: (phoneNumber: string) => (
      <p className="phone-num-preview">
        <PhoneIcon />
        {formatPhoneNumber(phoneNumber)}
      </p>
    ),
    span: (span: string) => {
      const numberValue = isNaN(+span) ? '0' : span;
      return (
        <span>{mapValueToMoneyFormat(convertMoneyToNumber(numberValue))}</span>
      );
    },
    p: (p: string) => <p>{p}</p>,
    slug: (p: string) => <p>{p}</p>,
    highlightlsSection: (listArray: string[], config: any) => {
      const { placeholder } = config || {};

      const placeholders = [
        `${placeholder || 'example'} 1`,
        `${placeholder || 'example'} 2`,
      ];

      return (
        <HighlightsSection
          listArray={listArray?.length ? listArray : placeholders}
          {...config}
        />
      );
    },
    advertCta: (ctaText: string) => (
      <Button onClick={() => {}}>
        <LinkIcon />
        {ctaText}
      </Button>
    ),
  };

const DetailPageField: React.FC<DetailPageFieldProps> = (props) => {
  const {
    className,
    previewType,
    name,
    field,
    icon,
    placeholder,
    label,
    allowEnter = false,
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [transitionClass, setTransitionClass] = useState('appear');

  const isFirst = useRef(true);

  const isSlugPreview = SLUG_FIELD_NAME === name;

  const {
    input: { value },
  } = useField(name);

  const classes = classNames(
    'detail-page-field',
    previewType,
    { isEditing, 'slug-preview': isSlugPreview },
    className,
  );

  const contentClasses = classNames(
    'detail-page-field__content',
    { isEditing },
    transitionClass,
  );

  const RenderPreview = useMemo(() => {
    const preview = label || value || placeholder;

    return FIELD_PREVIEW_ELEMENT[previewType](preview, {
      icon,
      placeholder,
      titleLabel: name,
    });
  }, [icon, label, name, placeholder, previewType, value]);

  const CorrectField = useMemo(
    () =>
      isSlugPreview
        ? ListingSlugField
        : previewType === 'highlightlsSection'
        ? TextareaArrayField
        : Field,
    [isSlugPreview, previewType],
  );

  const getChild = useCallback(
    () => (isEditing ? <CorrectField name={name} {...field} /> : RenderPreview),

    [CorrectField, RenderPreview, field, isEditing, name],
  );

  const [renderedChild, setRenderedChild] = useState(getChild());

  const onTransitionsEnd = useCallback(() => {
    setRenderedChild(getChild());

    if (isEditing) {
      const element = document.querySelector(`input[name="${name}"]`);
      if (element) (element as HTMLElement).focus();
    }

    setTransitionClass('appear');
  }, [isEditing, name, getChild]);

  const isSliderButtonClicked = useCallback((e) => {
    const target = e.target as Element | null;

    if (!target) return false;

    const classList = target.classList;

    return (
      target.closest('.ncoded-image-slider__arrow') ||
      classList.contains('ncoded-image-slider__arrow') ||
      target.closest('.ncoded-image-slider__dot-container__dot') ||
      classList.contains('ncoded-image-slider__dot-container__dot')
    );
  }, []);

  const onEnterKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (isEnterPressed(ev) && !allowEnter) {
        ev.preventDefault();
        setIsEditing(false);
      }
    },
    [allowEnter],
  );

  useEffect(() => {
    if (isEditing) return;
    setRenderedChild(RenderPreview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    setTransitionClass('disappear');
  }, [isEditing, name]);

  return (
    <div className={classes} onKeyDown={onEnterKeyDown}>
      <div
        className={contentClasses}
        onTransitionEnd={onTransitionsEnd}
        onClick={(e) => {
          if (!isEditing && !isSliderButtonClicked(e)) setIsEditing(true);
        }}
      >
        {renderedChild}
      </div>
      {isEditing && (
        <div
          className="detail-page-field__overlay"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

export default DetailPageField;
